import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Sparkles, BookHeart } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';
import { useAnalytics } from '@/hooks/useAnalytics';

type UpsellModalType = 
  | 'low_credits' 
  | 'no_credits' 
  | 'first_announcement' 
  | 'purchase_success' 
  | 'treslog_upsell'
  | 'post_publish_affirmation';

interface UpsellModalProps {
  type: UpsellModalType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creditsAdded?: number;
  recipientName?: string;
}

const modalContent: Record<string, Record<UpsellModalType, { title: string; body: string; primaryCta: string; secondaryCta: string; lucky?: string }>> = {
  en: {
    low_credits: { title: 'You have one more moment left', body: "You're almost out of Announcements. When you're ready to keep saying what matters, you can add more anytime.", primaryCta: 'Unlock more support', secondaryCta: 'Not right now' },
    no_credits: { title: "You've shared something meaningful", body: "You've used all your available Announcements. When you're ready to say more, we'll be right here.", primaryCta: 'Unlock more support', secondaryCta: "I'll come back later" },
    first_announcement: { title: 'You showed up for someone today.', body: 'That matters.', primaryCta: 'Keep going', secondaryCta: '', lucky: '{name} is lucky to have you.' },
    post_publish_affirmation: { title: 'You showed up for someone today.', body: 'That matters.', primaryCta: 'Keep going', secondaryCta: '', lucky: '{name} is lucky to have you.' },
    purchase_success: { title: 'Your Announcements are ready 💛', body: "Your words are saved. You can return to them anytime. You've added {{count}} Announcements to your account — they never expire.", primaryCta: 'Create a Living Announcement', secondaryCta: 'View My Announcements' },
    treslog_upsell: { title: 'You just shared something meaningful', body: "That moment mattered. If you'd like a private space to reflect on your own journey, TRESLOG is available with the Legacy plan.", primaryCta: 'Learn about Legacy', secondaryCta: 'Not right now' },
  },
  es: {
    low_credits: { title: 'Te queda un momento más', body: 'Casi te quedas sin Anuncios. Cuando estés listo para seguir diciendo lo que importa, puedes añadir más en cualquier momento.', primaryCta: 'Desbloquear más apoyo', secondaryCta: 'Ahora no' },
    no_credits: { title: 'Has compartido algo significativo', body: 'Has usado todos tus Anuncios disponibles. Cuando estés listo para decir más, estaremos aquí.', primaryCta: 'Desbloquear más apoyo', secondaryCta: 'Volveré después' },
    first_announcement: { title: 'Hoy te presentaste para alguien.', body: 'Eso importa.', primaryCta: 'Seguir adelante', secondaryCta: '', lucky: '{name} tiene suerte de tenerte.' },
    post_publish_affirmation: { title: 'Hoy te presentaste para alguien.', body: 'Eso importa.', primaryCta: 'Seguir adelante', secondaryCta: '', lucky: '{name} tiene suerte de tenerte.' },
    purchase_success: { title: 'Tus Anuncios están listos 💛', body: 'Tus palabras están guardadas. Puedes volver a ellas cuando quieras. Has añadido {{count}} Anuncios a tu cuenta — nunca expiran.', primaryCta: 'Crear un Anuncio Viviente', secondaryCta: 'Ver Mis Anuncios' },
    treslog_upsell: { title: 'Acabas de compartir algo significativo', body: 'Ese momento importó. Si te gustaría un espacio privado para reflexionar sobre tu propio camino, TRESLOG está disponible con el plan Legado.', primaryCta: 'Conocer el plan Legado', secondaryCta: 'Ahora no' },
  },
  fr: {
    low_credits: { title: 'Il vous reste un dernier moment', body: "Vous êtes presque à court d'Annonces. Quand vous serez prêt à continuer, vous pouvez en ajouter à tout moment.", primaryCta: 'Débloquer plus de soutien', secondaryCta: 'Pas maintenant' },
    no_credits: { title: 'Vous avez partagé quelque chose de significatif', body: "Vous avez utilisé toutes vos Annonces disponibles. Quand vous serez prêt à en dire plus, nous serons là.", primaryCta: 'Débloquer plus de soutien', secondaryCta: 'Je reviendrai plus tard' },
    first_announcement: { title: "Vous vous êtes montré pour quelqu'un aujourd'hui.", body: "C'est important.", primaryCta: 'Continuer', secondaryCta: '', lucky: '{name} a de la chance de vous avoir.' },
    post_publish_affirmation: { title: "Vous vous êtes montré pour quelqu'un aujourd'hui.", body: "C'est important.", primaryCta: 'Continuer', secondaryCta: '', lucky: '{name} a de la chance de vous avoir.' },
    purchase_success: { title: 'Vos Annonces sont prêtes 💛', body: "Vos mots sont sauvegardés. Vous pouvez y revenir à tout moment. Vous avez ajouté {{count}} Annonces — elles n'expirent jamais.", primaryCta: 'Créer une Annonce Vivante', secondaryCta: 'Voir Mes Annonces' },
    treslog_upsell: { title: "Vous venez de partager quelque chose de significatif", body: "Ce moment comptait. Si vous souhaitez un espace privé pour réfléchir à votre parcours, TRESLOG est disponible avec le plan Legacy.", primaryCta: 'Découvrir Legacy', secondaryCta: 'Pas maintenant' },
  },
  pt: {
    low_credits: { title: 'Você tem mais um momento', body: 'Você está quase sem Anúncios. Quando estiver pronto para continuar dizendo o que importa, pode adicionar mais a qualquer momento.', primaryCta: 'Desbloquear mais apoio', secondaryCta: 'Agora não' },
    no_credits: { title: 'Você compartilhou algo significativo', body: 'Você usou todos os seus Anúncios disponíveis. Quando estiver pronto para dizer mais, estaremos aqui.', primaryCta: 'Desbloquear mais apoio', secondaryCta: 'Volto depois' },
    first_announcement: { title: 'Você apareceu para alguém hoje.', body: 'Isso importa.', primaryCta: 'Continuar', secondaryCta: '', lucky: '{name} tem sorte de ter você.' },
    post_publish_affirmation: { title: 'Você apareceu para alguém hoje.', body: 'Isso importa.', primaryCta: 'Continuar', secondaryCta: '', lucky: '{name} tem sorte de ter você.' },
    purchase_success: { title: 'Seus Anúncios estão prontos 💛', body: 'Suas palavras estão salvas. Você pode voltar a elas a qualquer momento. Você adicionou {{count}} Anúncios — eles nunca expiram.', primaryCta: 'Criar um Anúncio Vivo', secondaryCta: 'Ver Meus Anúncios' },
    treslog_upsell: { title: 'Você acabou de compartilhar algo significativo', body: 'Esse momento importou. Se você gostaria de um espaço privado para refletir sobre sua jornada, TRESLOG está disponível com o plano Legacy.', primaryCta: 'Conhecer o Legacy', secondaryCta: 'Agora não' },
  },
  ar: {
    low_credits: { title: 'لديك لحظة واحدة أخرى', body: 'أنت على وشك نفاد الإعلانات. عندما تكون مستعداً لمواصلة قول ما يهم، يمكنك إضافة المزيد في أي وقت.', primaryCta: 'فتح المزيد من الدعم', secondaryCta: 'ليس الآن' },
    no_credits: { title: 'لقد شاركت شيئاً ذا معنى', body: 'لقد استخدمت جميع إعلاناتك المتاحة. عندما تكون مستعداً لقول المزيد، سنكون هنا.', primaryCta: 'فتح المزيد من الدعم', secondaryCta: 'سأعود لاحقاً' },
    first_announcement: { title: 'لقد حضرت لشخص ما اليوم.', body: 'هذا مهم.', primaryCta: 'متابعة', secondaryCta: '', lucky: '{name} محظوظ بوجودك.' },
    post_publish_affirmation: { title: 'لقد حضرت لشخص ما اليوم.', body: 'هذا مهم.', primaryCta: 'متابعة', secondaryCta: '', lucky: '{name} محظوظ بوجودك.' },
    purchase_success: { title: 'إعلاناتك جاهزة 💛', body: 'كلماتك محفوظة. يمكنك العودة إليها في أي وقت. لقد أضفت {{count}} إعلاناً — لا تنتهي صلاحيتها أبداً.', primaryCta: 'إنشاء إعلان حي', secondaryCta: 'عرض إعلاناتي' },
    treslog_upsell: { title: 'لقد شاركت شيئاً ذا معنى للتو', body: 'تلك اللحظة مهمة. إذا كنت ترغب في مساحة خاصة للتأمل في رحلتك، TRESLOG متاح مع خطة Legacy.', primaryCta: 'تعرف على Legacy', secondaryCta: 'ليس الآن' },
  },
  ja: {
    low_credits: { title: 'あと1つの瞬間が残っています', body: 'アナウンスメントが残りわずかです。大切なことを伝え続ける準備ができたら、いつでも追加できます。', primaryCta: 'もっとサポートを解放', secondaryCta: '今はしない' },
    no_credits: { title: '意味のあることを共有しました', body: '利用可能なアナウンスメントをすべて使いました。もっと伝える準備ができたら、ここにいます。', primaryCta: 'もっとサポートを解放', secondaryCta: 'また後で' },
    first_announcement: { title: '今日、あなたは誰かのために現れました。', body: 'それは大切なことです。', primaryCta: '続ける', secondaryCta: '', lucky: '{name}はあなたがいて幸せです。' },
    post_publish_affirmation: { title: '今日、あなたは誰かのために現れました。', body: 'それは大切なことです。', primaryCta: '続ける', secondaryCta: '', lucky: '{name}はあなたがいて幸せです。' },
    purchase_success: { title: 'アナウンスメントの準備ができました 💛', body: 'あなたの言葉は保存されました。いつでも戻れます。{{count}}件のアナウンスメントを追加しました — 有効期限はありません。', primaryCta: 'リビングアナウンスメントを作成', secondaryCta: 'アナウンスメントを見る' },
    treslog_upsell: { title: '意味のあることを共有したところです', body: 'あの瞬間は大切でした。自分の旅を振り返るプライベートな空間が欲しければ、TRESLOGはLegacyプランで利用可能です。', primaryCta: 'Legacyについて', secondaryCta: '今はしない' },
  },
  ko: {
    low_credits: { title: '한 순간이 더 남아 있습니다', body: '어나운스먼트가 거의 다 됐습니다. 중요한 말을 계속할 준비가 되면 언제든 추가할 수 있습니다.', primaryCta: '더 많은 지원 잠금 해제', secondaryCta: '지금은 아니요' },
    no_credits: { title: '의미 있는 것을 공유했습니다', body: '사용 가능한 모든 어나운스먼트를 사용했습니다. 더 말할 준비가 되면 여기 있겠습니다.', primaryCta: '더 많은 지원 잠금 해제', secondaryCta: '나중에 올게요' },
    first_announcement: { title: '오늘 누군가를 위해 나타났습니다.', body: '그것은 중요합니다.', primaryCta: '계속하기', secondaryCta: '', lucky: '{name}은(는) 당신이 있어 행운입니다.' },
    post_publish_affirmation: { title: '오늘 누군가를 위해 나타났습니다.', body: '그것은 중요합니다.', primaryCta: '계속하기', secondaryCta: '', lucky: '{name}은(는) 당신이 있어 행운입니다.' },
    purchase_success: { title: '어나운스먼트가 준비되었습니다 💛', body: '당신의 말은 저장되었습니다. 언제든 돌아올 수 있습니다. {{count}}개의 어나운스먼트를 추가했습니다 — 만료되지 않습니다.', primaryCta: '리빙 어나운스먼트 만들기', secondaryCta: '내 어나운스먼트 보기' },
    treslog_upsell: { title: '방금 의미 있는 것을 공유했습니다', body: '그 순간은 중요했습니다. 자신의 여정을 돌아볼 프라이빗 공간을 원하시면 TRESLOG는 Legacy 플랜에서 이용 가능합니다.', primaryCta: 'Legacy 알아보기', secondaryCta: '지금은 아니요' },
  },
  id: {
    low_credits: { title: 'Anda memiliki satu momen lagi', body: 'Pengumuman Anda hampir habis. Ketika siap untuk terus mengatakan yang penting, Anda bisa menambahkan kapan saja.', primaryCta: 'Buka lebih banyak dukungan', secondaryCta: 'Tidak sekarang' },
    no_credits: { title: 'Anda telah berbagi sesuatu yang bermakna', body: 'Anda telah menggunakan semua Pengumuman yang tersedia. Ketika siap untuk mengatakan lebih, kami ada di sini.', primaryCta: 'Buka lebih banyak dukungan', secondaryCta: 'Saya kembali nanti' },
    first_announcement: { title: 'Anda hadir untuk seseorang hari ini.', body: 'Itu penting.', primaryCta: 'Lanjutkan', secondaryCta: '', lucky: '{name} beruntung memiliki Anda.' },
    post_publish_affirmation: { title: 'Anda hadir untuk seseorang hari ini.', body: 'Itu penting.', primaryCta: 'Lanjutkan', secondaryCta: '', lucky: '{name} beruntung memiliki Anda.' },
    purchase_success: { title: 'Pengumuman Anda siap 💛', body: 'Kata-kata Anda tersimpan. Anda bisa kembali kapan saja. Anda telah menambahkan {{count}} Pengumuman — tidak pernah kedaluwarsa.', primaryCta: 'Buat Living Announcement', secondaryCta: 'Lihat Pengumuman Saya' },
    treslog_upsell: { title: 'Anda baru saja berbagi sesuatu yang bermakna', body: 'Momen itu penting. Jika Anda menginginkan ruang pribadi untuk merefleksikan perjalanan Anda, TRESLOG tersedia dengan paket Legacy.', primaryCta: 'Pelajari Legacy', secondaryCta: 'Tidak sekarang' },
  },
};

export function UpsellModal({ type, open, onOpenChange, creditsAdded, recipientName }: UpsellModalProps) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { trackTreslogViewed, trackLegacyPlanViewed } = useAnalytics();
  const content = modalContent[language]?.[type] || modalContent.en[type];

  // For affirmation types, don't show pricing - just emotional support
  const isAffirmationType = type === 'first_announcement' || type === 'post_publish_affirmation';

  const handlePrimaryCta = () => {
    onOpenChange(false);
    if (isAffirmationType) {
      navigate('/dashboard');
    } else if (type === 'purchase_success') {
      navigate('/create');
    } else if (type === 'treslog_upsell') {
      trackTreslogViewed('upsell');
      trackLegacyPlanViewed('treslog_upsell_modal');
      navigate('/#pricing');
    } else {
      // Scroll to pricing or navigate to pricing page
      const pricingSection = document.getElementById('pricing');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/#pricing');
      }
    }
  };

  const handleSecondaryCta = () => {
    onOpenChange(false);
    if (type === 'purchase_success') {
      navigate('/dashboard');
    }
  };

  // For purchase success, customize the body with the actual credit amount
  const displayBody =
    type === 'purchase_success' && creditsAdded
      ? content.body.replace('{{count}}', creditsAdded.toString())
      : content.body;

  const getIconType = () => {
    if (type === 'treslog_upsell') return 'treslog';
    if (isAffirmationType) return 'heart';
    if (type === 'low_credits' || type === 'no_credits') return 'heart';
    return 'sparkles';
  };

  const iconType = getIconType();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center space-y-4">
          <div className="mx-auto">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1, type: 'spring' }}
              className={`p-4 rounded-full ${
                iconType === 'heart'
                  ? 'bg-primary/10'
                  : iconType === 'treslog'
                  ? 'bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30'
                  : 'bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30'
              }`}
            >
              {iconType === 'heart' ? (
                <Heart className="h-8 w-8 text-primary" fill="currentColor" />
              ) : iconType === 'treslog' ? (
                <BookHeart className="h-8 w-8 text-amber-600 dark:text-amber-400" />
              ) : (
                <Sparkles className="h-8 w-8 text-amber-500" />
              )}
            </motion.div>
          </div>
          <DialogTitle className="text-xl font-semibold">{content.title}</DialogTitle>
          <DialogDescription className="text-base text-muted-foreground leading-relaxed">
            {displayBody}
          </DialogDescription>
          
          {/* Show recipient name for affirmation types */}
          {isAffirmationType && recipientName && content.lucky && (
            <p className="text-sm text-muted-foreground mt-2">
              {content.lucky.replace('{name}', recipientName)}
            </p>
          )}
        </DialogHeader>

        <div className="flex flex-col gap-3 mt-6">
          <Button onClick={handlePrimaryCta} className="w-full" size="lg">
            {content.primaryCta}
          </Button>
          {content.secondaryCta && (
            <Button
              onClick={handleSecondaryCta}
              variant="ghost"
              className="w-full text-muted-foreground"
            >
              {content.secondaryCta}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Hook for managing upsell modal state
export function useUpsellModal() {
  const [modalState, setModalState] = useState<{
    type: UpsellModalType;
    open: boolean;
    creditsAdded?: number;
    recipientName?: string;
  }>({
    type: 'low_credits',
    open: false,
  });

  const showLowCreditsModal = () => {
    setModalState({ type: 'low_credits', open: true });
  };

  const showNoCreditsModal = () => {
    setModalState({ type: 'no_credits', open: true });
  };

  const showFirstAnnouncementModal = () => {
    // Changed: Show affirmation instead of upsell
    setModalState({ type: 'post_publish_affirmation', open: true });
  };

  const showPostPublishAffirmation = (recipientName?: string) => {
    setModalState({ type: 'post_publish_affirmation', open: true, recipientName });
  };

  const showPurchaseSuccessModal = (creditsAdded: number) => {
    setModalState({ type: 'purchase_success', open: true, creditsAdded });
  };

  const showTreslogUpsellModal = () => {
    setModalState({ type: 'treslog_upsell', open: true });
  };

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, open: false }));
  };

  return {
    modalState,
    showLowCreditsModal,
    showNoCreditsModal,
    showFirstAnnouncementModal,
    showPostPublishAffirmation,
    showPurchaseSuccessModal,
    showTreslogUpsellModal,
    closeModal,
    setModalOpen: (open: boolean) => setModalState((prev) => ({ ...prev, open })),
  };
}
